import React, { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { mainText } from 'assets/styles/variables'
import { handleException } from 'clients/exception-handling'
import { Title } from 'components/Title'
import { conditionOptionsList, defaultValues } from 'features/alerts/constants'
import {
  alertFilterStateFromFiltersV2,
  alertFilterStateFromLegacyRequest,
  buildAlertPayload,
  emptyAlertFilterState,
} from 'features/alerts/features/create-alert/alert-filter-state'
import AlertSummaryCard from 'features/alerts/features/create-alert/components/alert-summary-card/AlertSummaryCard'
import {
  Body,
  Container,
  Form,
  FormWrapper,
} from 'features/alerts/features/create-alert/create-alert.styles'
import { CreateAlertProps } from 'features/alerts/features/create-alert/types'
import { useAlertChartQuery } from 'features/alerts/hooks/use-alert-chart-query'
import { useAlertSharedUsersQuery } from 'features/alerts/hooks/use-alert-shared-users-query'
import {
  AlertBody,
  AlertResponse,
  AlertType,
  useAlertService,
} from 'features/alerts/service'
import { AuthContext } from 'features/auth/auth-context'
import { useFilterUIStore } from 'features/filters/store'
import mixpanel from 'trackers/mixpanel'
import { UserInfo } from 'types/project'

const CreateAlert: React.FC<CreateAlertProps> = ({
  alertRecord = { alert: undefined, owners: [], viewers: [] },
  setIsOpen,
  alertRequest,
  projectId,
  isPublicViewer,
  route,
  isAlertDrawerOpen,
  onAlertUpserted,
}) => {
  const alertService = useAlertService()
  // The project's available filter options — the same store legacy
  // FilterButton/PanelBadges read internally, populated once per project by
  // `features/project/pages/Project.tsx`.
  const filterOptions = useFilterUIStore((state) => state.defaultFilterList)
  const { alert, owners: existingOwners, viewers } = alertRecord
  const authContext = useContext(AuthContext)
  const currentUserEmail = authContext?.user?.attributes?.email
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filterState, setFilterState] = useState(
    alert
      ? alertFilterStateFromFiltersV2(alert.filters_v2)
      : alertRequest
        ? alertFilterStateFromLegacyRequest(alertRequest)
        : emptyAlertFilterState,
  )
  const [selectedTriggerCategory, setSelectedTriggerCategory] = useState(
    conditionOptionsList.find((el) => el.value === alert?.trigger_type)?.type ??
      'outlier',
  )
  const [recipients, setRecipients] = useState<string[]>(
    alert ? viewers.map((el) => el.email) : [],
  )
  const [owners, setOwners] = useState<string[]>(
    alert
      ? existingOwners.map((el) => el.email)
      : currentUserEmail
        ? [currentUserEmail]
        : [],
  )

  const { data: sharedUsers } = useAlertSharedUsersQuery(
    alertService,
    projectId,
    !!projectId && !isPublicViewer,
  )

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
    trigger,
    watch,
  } = useForm<AlertType>({
    defaultValues: alert || defaultValues,
  })

  const onSubmit = async (formValues: AlertType): Promise<void> => {
    setIsSubmitting(true)
    const alertPayload: AlertBody = buildAlertPayload(
      formValues,
      filterState,
      recipients,
      'Live',
    )
    let message = 'Alert Created'
    // Sharing uses a single row per (user, alert) with an is_owner flag.
    // Unshare deletes the row entirely. To allow demotion (owner → viewer),
    // we must delete the owner row BEFORE the PUT reconciles the viewer set —
    // otherwise the PUT's viewer-add sees the existing owner row and skips it,
    // then unshare wipes the row last, leaving the user gone from the alert.
    // Order: unshare demoted owners → updateAlert (re-adds as viewers) → shareAlert (add owners).
    const existingOwnerEmails = existingOwners.map((el) => el.email)
    const ownerEmailsToRemove = existingOwnerEmails.filter(
      (email) => email !== currentUserEmail && !owners.includes(email),
    )
    if (alert && ownerEmailsToRemove.length > 0) {
      try {
        await alertService.unshareAlert(projectId, {
          alert_id: alert.id,
          email_addresses: ownerEmailsToRemove,
        })
      } catch (cause) {
        handleException(cause, { operation: 'alert.owners.remove' })

        toast.warning('Alert saved, but some owners could not be removed')
      }
    }
    try {
      let upsertedAlert: AlertResponse
      if (alert) {
        mixpanel.track('alerts', {
          action: 'edit',
          type: 'submit',
          value: 'new',
          ...route,
        })
        mixpanel.track('alerts', { action: 'update', value: alert?.id })
        message = 'Alert Updated'
        // `alert` (the pre-edit AlertType) still carries the legacy filter
        // fields required by that response type — spreading it forward
        // would silently re-persist a stale, pre-edit snapshot of them
        // alongside the filters_v2 this submit actually built. `alertPayload`
        // (built by `buildAlertPayload`) already carries `undefined` for each
        // of those keys, which — spread after `...alert` — wins over
        // `alert`'s stale values (JSON.stringify then omits the `undefined`
        // keys entirely). The backend writes whatever legacy fields a
        // request carries; it does not treat `filters_v2`'s presence as
        // "ignore the rest of the body".
        upsertedAlert = await alertService.updateAlert(projectId, {
          ...alert,
          ...alertPayload,
        } as unknown as AlertType)
      } else {
        mixpanel.track('alerts', {
          action: 'create',
          type: 'submit',
          value: 'new',
          ...route,
        })
        upsertedAlert = await alertService.saveAlert(projectId, alertPayload)
      }
      // Grant any newly-added owners (the creator is auto-made owner by the
      // backend, so we only send emails that are new relative to existingOwners).
      const ownerEmailsToAdd = owners.filter(
        (email) =>
          email !== currentUserEmail && !existingOwnerEmails.includes(email),
      )
      if (ownerEmailsToAdd.length > 0) {
        try {
          await alertService.shareAlert(projectId, {
            alert_id: upsertedAlert.alert.id,
            as_owners: true,
            email_addresses: ownerEmailsToAdd,
          })
        } catch (cause) {
          handleException(cause, { operation: 'alert.owners.add' })

          toast.warning('Alert saved, but some owners could not be added')
        }
      }

      // Build the optimistic cache entry from the roster the user just submitted.
      // upsertedAlert predates the share/unshare owner calls, so its owners/viewers
      // arrays are stale — using it directly causes a transiently-wrong count until
      // the background refetch corrects it. Resolve each submitted email to a
      // UserInfo (falling back to a placeholder for brand-new additions; the refetch
      // reconciles full_name/id invisibly).
      const knownUsers = new Map<string, UserInfo>()
      ;[...existingOwners, ...viewers].forEach((u) =>
        knownUsers.set(u.email, u),
      )
      const resolveUser = (email: string): UserInfo =>
        knownUsers.get(email) ?? { email, full_name: email, id: -1 }
      onAlertUpserted({
        ...upsertedAlert,
        owners: owners.map(resolveUser),
        viewers: recipients.map(resolveUser),
      })
      setIsSubmitting(false)
      setIsOpen(false)
      reset()
      setFilterState(emptyAlertFilterState)
      setRecipients([])
      setOwners([])
      toast.success(message)
    } catch (cause) {
      handleException(cause, { operation: 'alert.save' })

      setIsSubmitting(false)
      toast.error(`Error ${alert ? 'updating' : 'creating'} alert`)
    }
  }

  const watchedValues = watch()

  const isChartPreviewEnabled = (() => {
    if (!projectId || !!Object.keys(errors).length || !isAlertDrawerOpen)
      return false

    if (
      !watchedValues.trigger_type.includes('Change') &&
      (watchedValues.compare_value == null ||
        isNaN(watchedValues.compare_value))
    ) {
      return false
    }

    return true
  })()

  const { data: chartData, isLoading: chartIsLoading } = useAlertChartQuery(
    alertService,
    {
      projectId,
      alert_scope: watchedValues.alert_scope,
      alert_type: watchedValues.alert_type,
      compare_value: watchedValues.compare_value,
      trigger_type: watchedValues.trigger_type,
      time_interval: watchedValues.time_interval,
      filterState,
      enabled: isChartPreviewEnabled,
    },
  )

  return (
    <Container>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormWrapper>
          <Body>
            <Title
              style={{
                color: mainText,
                padding: '30px 30px 10px',
                margin: '-10px -27px 0',
              }}
            >
              Create an alert
            </Title>
            <AlertSummaryCard
              control={control}
              errors={errors}
              projectId={projectId}
              recipients={recipients}
              setRecipients={setRecipients}
              owners={owners}
              setOwners={setOwners}
              allUsers={
                sharedUsers
                  ? [
                      ...sharedUsers.admins,
                      ...sharedUsers.shared_users,
                      ...sharedUsers.yogi_employees,
                    ].sort()
                  : []
              }
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              selectedTriggerCategory={selectedTriggerCategory}
              setSelectedTriggerCategory={setSelectedTriggerCategory}
              alert={alert}
              filterState={filterState}
              setFilterState={setFilterState}
              filterOptions={filterOptions}
              alert_scope={watchedValues.alert_scope}
              compare_value={watchedValues.compare_value}
              time_interval={watchedValues.time_interval}
              trigger_type={watchedValues.trigger_type}
              reviews_filter={watchedValues.reviews_filter}
              isFormValid={!Object.keys(errors).length}
              chartData={chartData}
              chartIsLoading={chartIsLoading}
              isSubmitting={isSubmitting}
            />
          </Body>
        </FormWrapper>
      </Form>
    </Container>
  )
}

export default CreateAlert
